import { ConfigProvider, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { useCallback, useState } from "react";
import { translate } from "shared/languages";

const { Text } = Typography;

export type ResetDomain = "working_hours" | "class_assignment";

const DOMAIN_DEFAULT_LABEL: Record<ResetDomain, () => string> = {
  working_hours: () =>
    `${translate("tabs.working_hours")} → ${translate("class_settings.employee_management.all_day")}`,
  class_assignment: () =>
    `${translate("tabs.assigned_classes")} → ${translate("class_settings.employee_management.assignment_mode_all")}`,
};

// Unlike the other Employee Management warning modals (leave's
// reactivation scan, the per-instructor conflict checks), this one needs no
// API call — the reset it's about to trigger (updateBusinessPolicies,
// business.ts) is deterministic, not conditional on finding anything, so
// there's nothing to preview/fetch. Still built as the same declarative
// hook + rendered-element pattern the other warning modals use, for
// consistency, just without the async/loading plumbing that pattern
// otherwise needs.
const EmployeeManagementResetNoticeModal = ({
  open,
  domains,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  domains: ResetDomain[];
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <ConfigProvider theme={modalButtonsTheme}>
    <Modal
      open={open}
      title={
        <ModalHeader
          tone="amber"
          title={translate("class_settings.employee_management.reset_notice.title")}
        />
      }
      okText={translate("class_settings.employee_management.reset_notice.confirm")}
      cancelText={translate("button.cancel")}
      onOk={onConfirm}
      onCancel={onCancel}
      width={480}
      destroyOnClose
    >
      <Text style={{ ...modalSubtitleStyle, marginTop: 20 }}>
        {translate("class_settings.employee_management.reset_notice.description")}
      </Text>
      <ul style={{ marginTop: 12, paddingLeft: 20 }}>
        {domains.map((domain) => (
          <li key={domain}>
            <Text>{DOMAIN_DEFAULT_LABEL[domain]()}</Text>
          </li>
        ))}
      </ul>
    </Modal>
  </ConfigProvider>
);

/**
 * Gates the Employee Management settings save behind a heads-up when Jam
 * Kerja and/or Kelas yang Ditugaskan is about to newly turn on: any
 * already-configured instructor is about to be reset to the safe default,
 * with their prior settings archived (not deleted) and individually
 * restorable — see updateBusinessPolicies (business.ts) for where the
 * actual archive-and-reset happens. confirmAndProceed(domains, onConfirm)
 * shows the notice only when `domains` is non-empty; with none, it calls
 * onConfirm immediately, same "nothing to warn about" shortcut the other
 * warning modals use.
 */
export const useEmployeeManagementResetNotice = () => {
  const [pending, setPending] = useState<{
    domains: ResetDomain[];
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  const confirmAndProceed = useCallback(
    (domains: Set<ResetDomain>, onConfirm: () => void, onCancel?: () => void) => {
      if (domains.size === 0) {
        onConfirm();
        return;
      }
      setPending({ domains: Array.from(domains), onConfirm, onCancel });
    },
    [],
  );

  const resetNoticeModal = (
    <EmployeeManagementResetNoticeModal
      open={!!pending}
      domains={pending?.domains ?? []}
      onConfirm={() => {
        pending?.onConfirm();
        setPending(null);
      }}
      onCancel={() => {
        pending?.onCancel?.();
        setPending(null);
      }}
    />
  );

  return { confirmAndProceed, resetNoticeModal };
};
